import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type {
  EventHubProducerClient,
  TokenCredential,
} from '@azure/event-hubs';
import {
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import {
  ITrackEventPayload,
  ITrackMetricPayload,
  ITrackRequestPayload,
} from '@epicuro-next/models/platform/monitoring';
import { ConfigurationsService } from '@epicuro-next/platform/configuration';
import { isNonNullish } from '@epicuro-next/utilities';
import * as Sentry from '@sentry/angular';
import { combineLatest, of, Subject, Subscription } from 'rxjs';
import {
  bufferTime,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { AbstractAnalyticsService } from '../abstract/abstract-analytics.service';
import { REPORTING_TOKEN_API_TOKEN } from '../tokens';

import {
  AzureEventHubConfiguration,
  AzureEventHubConfigurationKeys,
  AZURE_EVENT_HUB_NAMESPACE,
  DEFAULT_EVENT_HUB_NAME,
  EVENT_HUB_DOMAIN,
} from './configurations';

@Injectable()
export class AzureEventhubService extends AbstractAnalyticsService {
  private config = inject(ConfigurationsService);
  private httpClient = inject(HttpClient);
  private url = inject(REPORTING_TOKEN_API_TOKEN);

  private eventBuffer$: Subject<
    ITrackEventPayload | ITrackRequestPayload | ITrackMetricPayload
  > = new Subject();

  private eventHubName$ = this.config.getConfigValue<
    PulseConfigurations,
    PulseConfigurationKeys.TrackingHostEventHubName
  >({
    featureName: PulseConfigurationKeys.TrackingHostEventHubName,
    namespace: PULSE_FEATURE_NAME,
    defaultValue: DEFAULT_EVENT_HUB_NAME,
  });

  private namespace$ = this.config.getConfigValue<
    PulseConfigurations,
    PulseConfigurationKeys.TrackingHostNamespace
  >({
    featureName: PulseConfigurationKeys.TrackingHostNamespace,
    namespace: PULSE_FEATURE_NAME,
    defaultValue: false,
  });

  private featureToggle$ = this.config.getConfigValue({
    featureName: PulseConfigurationKeys.EventHubTracking,
    namespace: PULSE_FEATURE_NAME,
    defaultValue: false,
  });

  private disabledEventNames$ = this.config
    .getConfigValue<
      AzureEventHubConfiguration,
      AzureEventHubConfigurationKeys.DisabledEventNames
    >({
      namespace: AZURE_EVENT_HUB_NAMESPACE,
      featureName: AzureEventHubConfigurationKeys.DisabledEventNames,
      defaultValue: [],
    })
    .pipe(
      distinctUntilChanged(
        // tslint:disable-next-line:no-misleading-array-reverse
        (a, b) => JSON.stringify(a.sort()) === JSON.stringify(b.sort()),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  private client?: EventHubProducerClient;
  private host = '';
  private hubName = '';
  private token: {
    token: string;
    expiresOnTimestamp: number;
  };
  private bufferSub: Subscription;

  constructor() {
    super();

    combineLatest([
      this.namespace$.pipe(filter(filterIfString)),
      this.eventHubName$,
    ])
      .pipe(take(1))
      .subscribe(([namespace, eventHubName]) => {
        this.initialize(namespace, eventHubName);
      });
  }

  private initialize(namespace: string, hubName: string) {
    this.host = `${namespace}.${EVENT_HUB_DOMAIN}`;
    this.hubName = hubName;

    this.bufferSub = this.eventBuffer$
      .pipe(
        bufferTime(1000),
        withLatestFrom(this.featureToggle$),
        filter(
          ([events, shouldSend]: [ITrackEventPayload[], boolean]) =>
            !!events.length && shouldSend,
        ),
        switchMap(([events]) => this.sendMetric(events)),
      )
      .subscribe();
  }

  private createTokenProvider(): TokenCredential {
    return {
      getToken: async () => {
        return !this.token?.expiresOnTimestamp || this.isTokenExpired()
          ? this.httpClient
              .post(this.url, {
                audience: `${this.host}/${this.hubName}`,
              })
              .pipe(
                map((response: { sas: string; expiry: number }) => ({
                  token: response.sas,
                  expiresOnTimestamp: response.expiry,
                })),
                tap((token) => {
                  this.token = token;
                }),
                catchError((e) => {
                  Sentry.captureException(e);
                  return of();
                }),
              )
              .toPromise()
          : new Promise(() => this.token);
      },
    } as TokenCredential;
  }

  /**
   * Lazily imports the Azure Event Hubs client the first time telemetry is
   * actually sent. This keeps the (large, AMQP-based) `@azure/event-hubs`
   * library out of the initial bundle and off the app's critical path; it is
   * only downloaded when Event Hub tracking is enabled and events are flushed.
   */
  private async getClient(): Promise<EventHubProducerClient> {
    let client = this.client;
    if (!client) {
      const eventHubs = await import('@azure/event-hubs');
      client = new eventHubs.EventHubProducerClient(
        this.host,
        this.hubName,
        this.createTokenProvider(),
      );
      this.client = client;
    }
    return client;
  }

  private async sendMetric(payload: unknown[]) {
    const client = await this.getClient();
    const eventData = payload.map((data) => ({ body: data }));
    return client.sendBatch(eventData);
  }

  public trackEvent(trackEventPayload: ITrackEventPayload) {
    this.disabledEventNames$
      .pipe(filter(isNonNullish), take(1))
      .subscribe((disabledEventNames = []) => {
        const bypassEvent = disabledEventNames.some(
          (name) => name === trackEventPayload.name,
        );

        if (!bypassEvent) {
          this.eventBuffer$.next(trackEventPayload);
        }
      });
  }

  public trackRequestMetrics(trackRequestPayload: ITrackRequestPayload) {
    this.eventBuffer$.next(trackRequestPayload);
  }

  public trackMetric(trackMetricPayload: ITrackMetricPayload) {
    this.eventBuffer$.next(trackMetricPayload);
  }

  public destroy() {
    this.bufferSub?.unsubscribe();
  }

  private isTokenExpired() {
    return Date.now() > this.token.expiresOnTimestamp * 1000;
  }
}

function filterIfString(value: string | boolean): value is string {
  return typeof value !== 'boolean';
}
